import axiosPrivate from 'config/axiosPrivate';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';

import { fetchClientNotification } from 'store/Client/ClientNotification/features';
import { clientOrderListSelector } from 'store/Client/ClientOrderList';
import { fetchClientOrderList } from 'store/Client/ClientOrderList/features';
import { UserSessionSelector, updateShowTutorial } from 'store/UserSession';

import { ORDERSTATUS } from 'utils/Constants';

import DownloadModal from 'components/shared/modal/DownloadModal';
import { VideoInstructionsModal } from 'components/shared/modal/VideoInstructionsModal';
import { ClientOrdersTable } from 'components/Client/ClientOrdersTable/ClientOrdersTable';

const navItems = [
  {
    text: 'Toutes les commandes',
    tab: ORDERSTATUS.ALL,
    ordersCountBgColor: 'bg-gray',
  },
  {
    text: 'Commandes en cours',
    tab: ORDERSTATUS.MERGED_INPROGRESS,
    ordersCountBgColor: 'bg-blue',
  },
  {
    text: 'Commandes validées',
    tab: ORDERSTATUS.VALID,
    ordersCountBgColor: 'bg-green',
  },
];

export default function DashboardPage() {
  const {
    isLoading,
    isError,
    allOrders,
    mergedInProgressOrders,
    validatedOrders,
  } = useSelector(clientOrderListSelector);
  const { user, showTutorial, flag } = useSelector(UserSessionSelector);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [showInstructions, setShowInstructions] = useState(true);
  const [isTutorialVisible, setIsTutorialVisible] = useState(false);
  const [showDownloadModal, setShowDownloadModel] = useState(false);

  const [activeTab, setActiveTab] = useState(ORDERSTATUS.MERGED_INPROGRESS);

  const orders = useMemo(
    () => ({
      [ORDERSTATUS.ALL]: allOrders,
      [ORDERSTATUS.MERGED_INPROGRESS]: mergedInProgressOrders,
      [ORDERSTATUS.VALID]: validatedOrders,
    }),
    [allOrders, mergedInProgressOrders, validatedOrders]
  );

  const currentOrders = orders[activeTab] || [];

  useEffect(() => {
    document.title = t('Typix - Mon tableau de bord');
    window.dispatchEvent(new Event('locationchange'));
    dispatch(fetchClientOrderList());
    dispatch(fetchClientNotification());
  }, [dispatch, t]);

  useEffect(() => {
    if (showTutorial && flag) {
      setIsTutorialVisible(true);
    } else {
      setIsTutorialVisible(false);
    }
  }, [flag, showTutorial]);

  const handleOnchange = () => {
    axiosPrivate.put('/user/deactivateTutorial').then(() => {
      setIsTutorialVisible(false);
      dispatch(updateShowTutorial(false));
    });
  };

  return (
    <div className="content-center">
      <ul className="nav nav-tabs nav-tabs-ct">
        {navItems.map((item, index) => (
          <li key={index} className="nav-item">
            <button
              onClick={() => setActiveTab(item.tab)}
              className={`nav-link d-flex justify-content-center align-items-center pb-2 ${
                item.tab === activeTab ? 'active' : ''
              }`}
              data-bs-toggle="tab"
              type="button"
              role="tab"
            >
              {t(item.text)}
              <div
                className={`box-circle ms-1 ${item.ordersCountBgColor}`}
                style={{
                  fontSize: 18,
                  width: 40,
                  height: 40,
                }}
              >
                {orders[item.tab].length ?? 0}
              </div>
            </button>
          </li>
        ))}
      </ul>

      <ClientOrdersTable
        orderStatus={activeTab}
        orders={currentOrders}
        isLoading={isLoading}
        isError={isError}
        setShowDownloadModel={setShowDownloadModel}
        user={user}
      />

      {isTutorialVisible && (
        <VideoInstructionsModal
          showInstruction={showInstructions}
          setShowInstruction={() => setShowInstructions(false)}
          showInfoBanner={true}
          user={user}
          handleOnchange={handleOnchange}
        />
      )}
      <DownloadModal
        open={showDownloadModal}
        handleClose={() => setShowDownloadModel(false)}
      />
    </div>
  );
}
